// Content shared into the app from another app ("Share to Bazzef"): text,
// links, photos and videos. Native code (ShareIntentPlugin on iOS and Android)
// hands over the text plus file paths; the files become File objects here and
// stay in memory until the user picks where they go (pages/ShareTarget.js):
// a message, a post or a Lamha.

import { Capacitor, registerPlugin } from '@capacitor/core';

const ShareIntent = registerPlugin('ShareIntent');

let current = null; // { text: string, files: File[] }
const listeners = new Set();

export const getSharedContent = () => current;

export const setSharedContent = (content) => {
  current = content;
  listeners.forEach((listener) => listener(current));
};

export const clearSharedContent = () => setSharedContent(null);

/** Returns an unsubscribe fn. */
export const subscribeSharedContent = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Take what was shared and clear it, so it is used once. */
export const takeSharedContent = () => {
  const content = current;
  current = null;
  return content;
};

const toFile = async ({ path, mimeType, name }) => {
  const res = await fetch(Capacitor.convertFileSrc(path));
  const blob = await res.blob();
  return new File([blob], name || path.split('/').pop(), { type: mimeType || blob.type });
};

/**
 * Read what the native side received, if anything, into the store.
 * Resolves the content, or null when nothing was shared.
 */
let loading = null;

export const loadNativeShare = () => {
  // The share screen and the launch handler may both ask; native hands it over once
  if (!loading) loading = readNativeShare().finally(() => { loading = null; });
  return loading;
};

const readNativeShare = async () => {
  if (!Capacitor.isNativePlatform()) return null;
  let res;
  try {
    res = await ShareIntent.getShareContent();
  } catch {
    return null; // app build without the plugin
  }
  const text = (res?.text || '').trim();
  const files = (await Promise.all((res?.files || []).map((file) => toFile(file).catch((err) => {
    console.warn('[Share] Could not read shared file:', file?.name, err);
    return null;
  })))).filter(Boolean);
  if (!text && !files.length) return null;
  const content = { text, files };
  setSharedContent(content);
  return content;
};

/** Android: a share arrived while the app was already open. Returns an unsubscribe fn. */
export const onNativeShare = (callback) => {
  if (!Capacitor.isNativePlatform()) return () => {};
  const handle = ShareIntent.addListener('shareReceived', callback);
  return () => {
    Promise.resolve(handle).then((h) => h?.remove()).catch(() => {});
  };
};
